import { useTweaks } from '../components/Tweaks.jsx';
import { Header } from '../components/Header.jsx';
import { Footer } from '../components/Blocks.jsx';
import { LegalDoc } from '../components/Legal.jsx';

const TWEAK_DEFAULTS = {
  navWelshResources: 'Welsh Resources',
  navAboutUs: 'About us',
  navContact: 'Contact',
  startLearning: 'Start Learning',

  legalKicker: 'Legal',
  legalTitle: 'Refunds.',
  legalLede: "If you've put the work in and it hasn't clicked for you, we'll refund you. No interrogation, no retention gauntlet – we'd rather have your honest feedback than your reluctant money.",
  legalLastUpdated: 'Last updated: 14 April 2026',
  legalReadTime: '~3 min read',
  legalIntro: "Every learner is different and the method doesn't click for everyone. That's fine – but we don't want to keep your money if it hasn't worked. This page sets out exactly what we'll refund, when, and how to ask.",
  legalContactNote: "Got a refund situation that doesn't fit any of the categories below? Write to admin@saysomethingin.com and we'll do our best to sort it out personally.",

  footerCta: 'Start your language learning journey today.',
  footerCol1: 'What we do',
  footerCol2: 'SaySomethingin Learners',
  footerLegal: '© 2026 SaySomethingIn Cyf. Glaslyn, Ffordd y Parc, Bangor, Gwynedd LL57 4FE, Wales.',
};

const GROUPS = [
  { name: 'Header', keys: ['navWelshResources','navAboutUs','navContact','startLearning'] },
  { name: 'Document head', keys: ['legalKicker','legalTitle','legalLede','legalLastUpdated','legalReadTime','legalIntro','legalContactNote'], long:['legalLede','legalIntro','legalContactNote'] },
  { name: 'Footer', keys: ['footerCta','footerCol1','footerCol2','footerLegal'], long:['footerLegal'] },
];

const SECTIONS = [
  {
    id: 'the-seven-day-trial',
    heading: 'The 7-day free trial',
    body: [
      'Every new subscription starts with a 7-day free trial. No card is charged until day 8. Cancel any time during the trial from your account settings and you pay nothing.',
      "If you accidentally let the trial roll over into a paid subscription and you didn't mean to, email us within 7 days of the first charge and we'll refund it in full.",
    ],
  },
  {
    id: 'put-the-work-in-refund',
    heading: "The 'put the work in' refund",
    body: [
      "If you've done at least a week of lessons and the method genuinely hasn't clicked for you, we'll give you a full refund on your first month (monthly plan) or first two months pro-rata (annual plan).",
      "Email admin@saysomethingin.com with a line about what you tried and what didn't work. You're not auditioning – we read the feedback carefully because it helps us make the course better, but we'll refund you either way.",
      "One per person, please. We can't keep refunding the same account repeatedly.",
    ],
  },
  {
    id: 'monthly-subscriptions',
    heading: 'Monthly subscriptions',
    body: [
      "After the trial and the 'put the work in' window, monthly subscriptions aren't usually refundable for the month you're in – but you can cancel any time from your account settings and you won't be charged again.",
      "The exception: if you're charged after cancelling (which shouldn't happen, but systems are systems), email us and we'll refund it straight away.",
    ],
  },
  {
    id: 'annual-subscriptions',
    heading: 'Annual subscriptions',
    body: [
      "Annual plans come with a 30-day no-questions refund window from the date of first payment. After that, we don't refund the remainder of the year, but you can cancel auto-renewal any time – you'll keep access until the end of the year you paid for.",
      "If something has genuinely gone wrong – you bought the wrong plan, you were charged twice, your circumstances changed dramatically mid-year – write to us. We're human beings and we'll try to help.",
    ],
  },
  {
    id: 'gift-vouchers',
    heading: 'Gift vouchers',
    body: [
      "Unredeemed gift vouchers are refundable to the buyer within 14 days of purchase, in line with UK consumer law. After 14 days, or once the voucher has been redeemed, we can't refund the voucher itself – but the recipient can still use the 'put the work in' refund once they start their subscription.",
    ],
  },
  {
    id: 'one-to-one-coaching',
    heading: '1:1 coaching with Aran',
    body: [
      "Coaching sessions are booked individually. Cancel or reschedule more than 48 hours before the session and we'll refund in full. Within 48 hours, we retain 50% to cover the slot; within 12 hours, we can't refund, because the slot is gone.",
      "If Aran needs to reschedule (illness, a filming-day conflict), we'll either move the slot or refund you in full, your choice.",
    ],
  },
  {
    id: 'how-refunds-are-paid',
    heading: 'How refunds are paid',
    body: [
      "Refunds go back to the original card or payment method. Stripe usually delivers them within 5–10 working days. We can't refund to a different card, PayPal account, or bank transfer – that's a fraud-prevention thing, sorry.",
      "If the card you paid with has expired or been cancelled, email us and we'll work out the best way to get the money back to you.",
    ],
  },
  {
    id: 'how-to-ask',
    heading: 'How to ask',
    body: [
      "Email admin@saysomethingin.com with 'Refund' in the subject and a line about which category you're in (trial rollover, first-month, annual cancellation, gift voucher, coaching, other). A human replies within one working day, usually the same day.",
      "No refund requires a phone call, a form, or jumping through hoops. If someone ever asks you to, something's gone wrong – email us and we'll fix it.",
    ],
  },
];

export default function Refunds() {
  const { tweaks, panel } = useTweaks({ defaults: TWEAK_DEFAULTS, groups: GROUPS });
  return (
    <div data-screen-label="Refunds">
      <Header t={tweaks}/>
      <main>
        <LegalDoc t={tweaks} sections={SECTIONS}/>
      </main>
      <Footer t={tweaks} hideCta={true}/>
      {panel}
    </div>
  );
}
